import React, { useState, useEffect } from 'react';
import { settingsService } from '../../services/settingsService';

const AlertSettings: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [aiFilterEnabled, setAiFilterEnabled] = useState(false);
  const [jmsValue, setJmsValue] = useState(70);
  const [autoApplyEnabled, setAutoApplyEnabled] = useState(false);
  const [showSuccess, setShowSuccess] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setError(null);
      const profile = await settingsService.getProfile();
      if (profile && profile.jms !== undefined) {
        if (profile.jms > 0) {
          setAiFilterEnabled(true);
          setJmsValue(profile.jms);
        } else {
          setAiFilterEnabled(false);
          setJmsValue(70); // Default threshold when disabled
        }
      }
      if (profile && profile.baa !== undefined) {
        setAutoApplyEnabled(profile.baa);
      }
    } catch (error) {
      console.error('Error loading profile:', error);
      setError('Failed to load settings. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleFilter = async (enabled: boolean) => {
    setAiFilterEnabled(enabled);
    if (!enabled) {
      // Immediately save jms = 0 when disabled
      setSaving(true);
      setError(null);
      try {
        await settingsService.setJMS(0);
        // Disabling JMS forces auto-apply off — server-side gate enforces this anyway,
        // but we keep the UI in sync without an extra round-trip.
        if (autoApplyEnabled) {
          await settingsService.setBAA(false);
          setAutoApplyEnabled(false);
        }
        setShowSuccess('AI filtering disabled');
        setTimeout(() => setShowSuccess(null), 3000);
      } catch (error) {
        console.error('Error disabling AI filter:', error);
        setError('Failed to disable AI filtering. Please try again.');
        setAiFilterEnabled(true); // Revert on error
      } finally {
        setSaving(false);
      }
    }
  };

  // Local-only toggle. The actual /api/set-baa POST happens in handleSave, alongside
  // /api/set-jms — this matters because the BAA endpoint server-side requires JMS > 0,
  // so saving BAA before JMS would 400 when the user just enabled JMS but hasn't saved yet.
  const handleToggleAutoApply = (enabled: boolean) => {
    if (!aiFilterEnabled) return; // gated by JMS
    setAutoApplyEnabled(enabled);
  };

  const handleSave = async () => {
    if (!aiFilterEnabled) return;

    // Validate jmsValue
    if (jmsValue < 0 || jmsValue > 100) {
      setError('Score must be between 0 and 100');
      return;
    }

    setSaving(true);
    setError(null);
    try {
      // Order matters: JMS first so the server's BAA gate (`profile.JMS > 0`) is
      // satisfied when /api/set-baa is hit immediately after.
      await settingsService.setJMS(jmsValue);
      await settingsService.setBAA(autoApplyEnabled);
      setShowSuccess('Alert settings saved successfully!');
      setTimeout(() => setShowSuccess(null), 3000);
    } catch (error) {
      console.error('Error saving alert settings:', error);
      setError('Failed to save settings. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-jobesta-blue"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Success Overlay */}
      {showSuccess && (
        <div className="fixed top-4 right-4 bg-green-50 border border-green-200 rounded-jobesta p-4 shadow-lg z-50 animate-fade-in">
          <div className="flex items-center gap-2">
            <svg className="w-5 h-5 text-green-600" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            <span className="text-green-800 font-medium">{showSuccess}</span>
          </div>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-jobesta p-4">
          <div className="flex items-center gap-2">
            <svg className="w-5 h-5 text-red-600" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
            <span className="text-red-800">{error}</span>
          </div>
        </div>
      )}

      {/* AI Job Filtering Section */}
      <div className="bg-white rounded-jobesta shadow-jobesta-card p-6 border border-jobesta-gray-40">
        <h2 className="text-lg font-semibold text-jobesta-gray-90 mb-4">AI Job Filtering</h2>

        {/* Toggle Checkbox */}
        <label className="flex items-center gap-3 cursor-pointer mb-4">
          <input
            type="checkbox"
            checked={aiFilterEnabled}
            onChange={(e) => handleToggleFilter(e.target.checked)}
            disabled={saving}
            className="w-5 h-5 text-jobesta-blue border-jobesta-gray-40 rounded focus:ring-jobesta-blue"
          />
          <span className="text-sm font-medium text-jobesta-gray-80">
            Use AI to filter job alert results
          </span>
        </label>

        {/* Description */}
        <p className="text-sm text-jobesta-gray-60 mb-6">
          When enabled, each job in your alerts will be scored against your profile.
          Only jobs matching at or above your threshold will be included.
        </p>

        {/* Minimum Score Input */}
        <div className="space-y-2 mb-6">
          <label className="block text-sm font-medium text-jobesta-gray-80">
            Minimum Match Score
          </label>
          <div className="flex items-center gap-3">
            <input
              type="number"
              min={0}
              max={100}
              value={jmsValue}
              onChange={(e) => setJmsValue(parseInt(e.target.value) || 0)}
              disabled={!aiFilterEnabled || saving}
              className={`w-24 input-jobesta ${!aiFilterEnabled ? 'bg-jobesta-gray-20 text-jobesta-gray-50 cursor-not-allowed' : ''}`}
            />
            <span className="text-sm text-jobesta-gray-60">(0-100)</span>
          </div>
        </div>

        {/* Warning Note */}
        <div className="bg-amber-50 border border-amber-200 rounded-jobesta p-4 mb-6">
          <div className="flex items-start gap-2">
            <svg className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
            <span className="text-sm text-amber-800">
              Note: This uses your profile matching quota.
            </span>
          </div>
        </div>

        {/* Save Button */}
        <button
          onClick={handleSave}
          disabled={!aiFilterEnabled || saving}
          className={`btn-jobesta-primary px-6 py-2 ${!aiFilterEnabled || saving ? 'opacity-50 cursor-not-allowed' : ''}`}
        >
          {saving ? 'Saving...' : 'Save Settings'}
        </button>

        {/* Auto-apply (BAA) checkbox — gated by JMS */}
        <div className="mt-6 pt-6 border-t border-jobesta-gray-30">
          <label className={`flex items-start gap-3 ${aiFilterEnabled ? 'cursor-pointer' : 'cursor-not-allowed opacity-60'}`}>
            <input
              type="checkbox"
              checked={autoApplyEnabled && aiFilterEnabled}
              onChange={(e) => handleToggleAutoApply(e.target.checked)}
              disabled={!aiFilterEnabled || saving}
              className="w-5 h-5 mt-0.5 text-jobesta-blue border-jobesta-gray-40 rounded focus:ring-jobesta-blue"
            />
            <div>
              <span className="text-sm font-medium text-jobesta-gray-80">
                🤖 Auto-apply to matched jobs (beta)
              </span>
              <p className="text-xs text-jobesta-gray-60 mt-1">
                When enabled, the bot will tailor a resume + cover letter and submit your application
                automatically for jobs above your minimum match score on supported boards. You'll still
                receive your alert email; auto-applied jobs appear in your job tracker with a 🤖 badge.
              </p>
              {!aiFilterEnabled && (
                <p className="text-xs text-amber-700 mt-1">
                  Enable AI Job Filtering above to use auto-apply.
                </p>
              )}
            </div>
          </label>
        </div>
      </div>
    </div>
  );
};

export default AlertSettings;
